import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Grid } from "antd";
import dayjs from "dayjs";
import { useGetBookingDetailQuery } from "src/store/slices/hotels/Hotels";
import { getMealTypeLabel, getCancellationInfo } from "src/Modules/Hotels/utils/room";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import { withConditionalComponent } from "src/HOC/WithMobileComponent";
import NoData from "@Components/NoData/NoData";
import { useTranslation } from "react-i18next";
import HotelTransactionDetailsSkeleton from "./components/HotelTransactionDetailsSkeleton/HotelTransactionDetailsSkeleton";
import { formatNumber } from "src/utility";
import HotelTransactionDetailsWeb from "./layouts/HotelTransactionDetails.web";
import HotelTransactionDetailsMob from "./layouts/HotelTransactionDetails.mob";
import i18n from "src/i18n";
import { formatDate } from "src/utility/time";

const HotelTransactionDetailsLayout = withConditionalComponent(HotelTransactionDetailsWeb, HotelTransactionDetailsMob);

function formatGuestName(title: string | null, firstName: string | null, lastName: string | null, type: string | null): string {
  const parts = [title, firstName, lastName].filter(Boolean);
  const name = parts.join(" ");
  return type === "Child" ? name + " " + i18n.t("UserHistory.hotel.childSuffix") : name;
}

export default function HotelTransactionDetails() {
  const { id } = useParams();
  const { data, isFetching, error } = useGetBookingDetailQuery({ referenceNumber: id ?? "" }, { skip: !id });

  const payment = useMemo(() => {
    if (!data) return { pointsRedeemed: "—", cashPaid: "—", taxes: "—", total: "—", processedOn: "—" };
    return {
      pointsRedeemed: data.pointsUsed > 0 ? `${formatNumber(data.pointsUsed)} ${i18n.t("UserHistory.ordersList.pts")}` : "—",
      cashPaid: data.amount > 0 ? `${data.currency} ${formatNumber(data.amount)}` : "—",
      taxes: data.totalTax > 0 ? `${data.currency} ${formatNumber(data.totalTax)}` : "—",
      total: data.totalFare > 0 ? `${data.currency} ${formatNumber(data.totalFare)}` : "—",
      processedOn: data.bookingDate ? formatDate(data.bookingDate, "D MMMM YYYY") : "—",
    };
  }, [data]);

  const hotelDetails = useMemo(() => {
    if (!data) return null;
    const address = [data.hotelDetails?.addressLine1, data.hotelDetails?.addressLine2].filter(Boolean).join(", ");
    const checkInDate = data.checkIn ? dayjs(data.checkIn) : null;
    const checkOutDate = data.checkOut ? dayjs(data.checkOut) : null;
    const nights =
      checkInDate && checkOutDate && checkInDate.isValid() && checkOutDate.isValid()
        ? Math.max(1, checkOutDate.diff(checkInDate, "day"))
        : null;
    const allGuests: string[] = [];
    for (const room of data.rooms ?? []) {
      for (const cd of room.customerDetails ?? []) {
        for (const cn of cd.customerNames ?? []) {
          allGuests.push(formatGuestName(cn.title, cn.firstName, cn.lastName, cn.type));
        }
      }
    }
    return {
      name: data.hotelDetails?.hotelName,
      address,
      nights: nights ? (nights > 1 ? i18n.t("UserHistory.hotel.nightsCount", { count: nights }) : i18n.t("UserHistory.hotel.nightCount", { count: nights })) : "—",
      rooms:
        Math.max(1, data.noOfRooms) > 1
          ? i18n.t("UserHistory.hotel.roomsCount", { count: Math.max(1, data.noOfRooms) })
          : i18n.t("UserHistory.hotel.roomCount", { count: Math.max(1, data.noOfRooms) }),
      roomsData: Object.entries(
        (data.rooms ?? []).reduce<Record<string, typeof data.rooms>>((acc, room) => {
          const key = room.name?.[0] ?? i18n.t("UserHistory.hotel.room");
          if (!acc[key]) acc[key] = [];
          acc[key].push(room);
          return acc;
        }, {}),
      ).map(([, rooms]) => {
        const cancellation = getCancellationInfo(rooms[0].cancelPolicies, rooms[0].isRefundable);
        const cancellationTags: { label: string; tone: "success" | "danger" }[] = [];
        if (cancellation.hasFreeCancellation && cancellation.freeCancelUntil) {
          cancellationTags.push({ label: i18n.t("UserHistory.hotel.freeCancellationUntil", { date: cancellation.freeCancelUntil }), tone: "success" });
        } else if (cancellation.hasFreeCancellation) {
          cancellationTags.push({ label: i18n.t("UserHistory.hotel.freeCancellation"), tone: "success" });
        } else if (!cancellation.refundable) {
          cancellationTags.push({ label: i18n.t("UserHistory.hotel.nonRefundable"), tone: "danger" });
        }
        for (const policy of cancellation.paidPolicies) {
          const isPercentage = policy.chargeType === "Percentage";
          cancellationTags.push({
            label: i18n.t(isPercentage ? "Hotels.common.cancellationChargePercentage" : "Hotels.common.cancellationChargeAmount", {
              date: policy.fromDate,
              charge: isPercentage ? policy.cancellationCharge : formatNumber(policy.cancellationCharge),
              currency: policy.currency ?? data.currency,
            }),
            tone: "danger",
          });
        }
        return {
          title: rooms[0].name?.[0] ?? i18n.t("UserHistory.hotel.room"),
          count: rooms.length,
          mealPlan: rooms[0].mealType ? getMealTypeLabel(rooms[0].mealType) : null,
          cancellationTags,
        };
      }),
      guests: allGuests,
    };
  }, [data]);

  const rateConditions = useMemo(() => data?.rateConditions ?? [], [data]);

  const { t } = useTranslation();
  const isLargeScreen = !!Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];

  if (isFetching) {
    return (
      <div className="flex w-full flex-1 items-start justify-center md:p-5">
        <HotelTransactionDetailsSkeleton />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex w-full flex-1 items-center justify-center">
        <NoData description={t("UserHistory.orderDetails.detailsUnavailable")} className="h-full w-full" />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-1 items-start justify-center md:p-5">
      <HotelTransactionDetailsLayout trigger={isLargeScreen} props={{ data, hotelDetails, payment, rateConditions }} />
    </div>
  );
}
